import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { listMapLayers, createMapLayer, updateMapLayer } from '../services/mapLayersService';
import type {
  MapLayer,
  MapLayerGeometryType,
  MapLayerFeatureStyle,
  MapLayerFillStyle,
  MapLayerLineStyle,
  MapLayerCircleStyle,
} from '../types';

const GEOMETRY_TYPES: MapLayerGeometryType[] = [
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
  'GeometryCollection',
  'Raster',
  'Unknown',
];

const EMPTY_LAYER: Omit<MapLayer, 'id'> = {
  name: '',
  url: '',
  category: '',
  categoryDescription: '',
  enabled: false,
  order: 0,
  minZoomLevel: undefined,
  maxZoomLevel: undefined,
  sourceLayerId: undefined,
  geometryType: undefined,
  displayAttributes: undefined,
  fillStyle: undefined,
  lineStyle: undefined,
  circleStyle: undefined,
  rasterStyle: undefined,
  featureStyleKey: undefined,
  featureStyles: undefined,
};

export default function MapLayerEditPage() {
  const { layerId } = useParams<{ layerId: string }>();
  const navigate = useNavigate();
  const isNew = layerId === 'new';

  const [id, setId] = useState('');
  const [form, setForm] = useState<Omit<MapLayer, 'id'>>(EMPTY_LAYER);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isNew && layerId) {
      loadLayer(layerId);
    }
  }, [layerId]);

  const loadLayer = async (id: string) => {
    try {
      const layers = await listMapLayers();
      const layer = layers.find(l => l.id === id);
      if (!layer) {
        setError('Layer not found');
        return;
      }
      setId(layer.id);
      const { id: _id, ...rest } = layer;
      setForm(rest);
    } catch (err) {
      setError('Failed to load layer');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError(null);

    const effectiveId = isNew ? id.trim() : layerId!;
    if (!effectiveId) {
      setError('ID is required');
      return;
    }
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!form.url.trim()) {
      setError('URL is required');
      return;
    }
    if (!form.category.trim()) {
      setError('Category is required');
      return;
    }

    const layer: MapLayer = { ...form, id: effectiveId };

    setSaving(true);
    try {
      if (isNew) {
        await createMapLayer(layer);
      } else {
        await updateMapLayer(effectiveId, layer);
      }
      navigate('/settings/map-layers');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save layer');
    } finally {
      setSaving(false);
    }
  };

  const featureStyles: MapLayerFeatureStyle[] = form.featureStyles ?? [];

  const setFeatureStyles = (updater: (prev: MapLayerFeatureStyle[]) => MapLayerFeatureStyle[]) => {
    setForm(prev => ({ ...prev, featureStyles: updater(prev.featureStyles ?? []) }));
  };

  const addFeatureStyleRow = () => {
    setFeatureStyles(prev => [...prev, { value: '', label: '' }]);
  };

  const updateFeatureStyle = (index: number, patch: Partial<MapLayerFeatureStyle>) => {
    setFeatureStyles(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const updateFeatureStyleFill = (index: number, patch: Partial<MapLayerFillStyle>) => {
    setFeatureStyles(prev =>
      prev.map((row, i) =>
        i === index ? { ...row, fillStyle: { ...(row.fillStyle ?? {}), ...patch } } : row
      )
    );
  };

  const updateFeatureStyleLine = (index: number, patch: Partial<MapLayerLineStyle>) => {
    setFeatureStyles(prev =>
      prev.map((row, i) =>
        i === index ? { ...row, lineStyle: { ...(row.lineStyle ?? {}), ...patch } } : row
      )
    );
  };

  const updateFeatureStyleCircle = (index: number, patch: Partial<MapLayerCircleStyle>) => {
    setFeatureStyles(prev =>
      prev.map((row, i) =>
        i === index ? { ...row, circleStyle: { ...(row.circleStyle ?? {}), ...patch } } : row
      )
    );
  };

  const removeFeatureStyle = (index: number) => {
    setFeatureStyles(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">Settings / Map Layers</div>
          <h2 className="page-title">{isNew ? 'Add Layer' : `Edit: ${id}`}</h2>
        </div>
      </div>

      {error && <div className="alert alert-danger mt-3">{error}</div>}

      {/* ── Basic ── */}
      <div className="card mt-3">
        <div className="card-header">
          <h3 className="card-title">Basic</h3>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* Row 1: ID, Order, Name, Geometry Type */}
            <div className="col-md-2">
              <label className="form-label">ID</label>
              <input
                type="text"
                className="form-control"
                value={isNew ? id : layerId}
                onChange={e => setId(e.target.value)}
                readOnly={!isNew}
                placeholder="e.g. srup_zec"
              />
            </div>
            <div className="col-md-1">
              <label className="form-label">Order</label>
              <input
                type="number"
                className="form-control"
                value={form.order}
                onChange={e => setForm(p => ({ ...p, order: Number(e.target.value) }))}
              />
            </div>
            <div className="col-md-5">
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-control"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Geometry Type</label>
              <select
                className="form-select"
                value={form.geometryType ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    geometryType: (e.target.value as MapLayerGeometryType) || undefined,
                  }))
                }
              >
                <option value="">— Select —</option>
                {GEOMETRY_TYPES.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* Row 2: URL, Display Attributes */}
            <div className="col-md-6">
              <label className="form-label">URL</label>
              <input
                type="text"
                className="form-control"
                value={form.url}
                onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
                placeholder="OGC: https://ogcapi.dgterritorio.gov.pt — WMS (Raster): full tile URL template with {bbox-epsg-3857}"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Display Attributes (comma-separated)</label>
              <input
                type="text"
                className="form-control"
                value={form.displayAttributes?.join(', ') ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    displayAttributes: e.target.value
                      ? e.target.value
                          .split(',')
                          .map(s => s.trim())
                          .filter(Boolean)
                      : undefined,
                  }))
                }
                placeholder="e.g. designacao, municipio"
              />
            </div>

            {/* Row 3: Category, Category Description */}
            <div className="col-md-4">
              <label className="form-label">Category</label>
              <input
                type="text"
                className="form-control"
                value={form.category}
                onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
              />
            </div>
            <div className="col-md-8">
              <label className="form-label">Category Description</label>
              <input
                type="text"
                className="form-control"
                value={form.categoryDescription ?? ''}
                onChange={e => setForm(p => ({ ...p, categoryDescription: e.target.value }))}
              />
            </div>

            {/* Row 4: Min Zoom, Max Zoom, Source Layer ID, Enabled */}
            <div className="col-md-2">
              <label className="form-label">Min Zoom</label>
              <input
                type="number"
                className="form-control"
                value={form.minZoomLevel ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    minZoomLevel: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Max Zoom</label>
              <input
                type="number"
                className="form-control"
                value={form.maxZoomLevel ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    maxZoomLevel: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Source Layer ID</label>
              <input
                type="text"
                className="form-control"
                value={form.sourceLayerId ?? ''}
                onChange={e => setForm(p => ({ ...p, sourceLayerId: e.target.value || undefined }))}
                placeholder="If different from layer ID"
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <label className="form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={form.enabled}
                  onChange={e => setForm(p => ({ ...p, enabled: e.target.checked }))}
                />
                <span className="form-check-label">Enabled</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* ── Styles ── */}
      <div className="card mt-3">
        <div className="card-header">
          <h3 className="card-title">Styles</h3>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* Fill Style */}
            <div className="col-12">
              <h4 className="text-muted fs-5 mb-0">Fill Style</h4>
            </div>
            <div className="col-md-3">
              <label className="form-label">Fill Color</label>
              <div className="input-group">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={form.fillStyle?.fillColor ?? '#cccccc'}
                  onChange={e =>
                    setForm(p => ({
                      ...p,
                      fillStyle: { ...(p.fillStyle ?? {}), fillColor: e.target.value },
                    }))
                  }
                />
                <input
                  type="text"
                  className="form-control"
                  value={form.fillStyle?.fillColor ?? ''}
                  onChange={e =>
                    setForm(p => ({
                      ...p,
                      fillStyle: { ...(p.fillStyle ?? {}), fillColor: e.target.value },
                    }))
                  }
                  placeholder="#cccccc"
                />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">Fill Opacity</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.1}
                className="form-control"
                value={form.fillStyle?.fillOpacity ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    fillStyle: {
                      ...(p.fillStyle ?? {}),
                      fillOpacity: e.target.value ? Number(e.target.value) : undefined,
                    },
                  }))
                }
                placeholder="0–1"
              />
            </div>

            {/* Line Style */}
            <div className="col-12">
              <h4 className="text-muted fs-5 mb-0">Line Style</h4>
            </div>
            <div className="col-md-3">
              <label className="form-label">Line Color</label>
              <div className="input-group">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={form.lineStyle?.lineColor ?? '#000000'}
                  onChange={e =>
                    setForm(p => ({
                      ...p,
                      lineStyle: { ...(p.lineStyle ?? {}), lineColor: e.target.value },
                    }))
                  }
                />
                <input
                  type="text"
                  className="form-control"
                  value={form.lineStyle?.lineColor ?? ''}
                  onChange={e =>
                    setForm(p => ({
                      ...p,
                      lineStyle: { ...(p.lineStyle ?? {}), lineColor: e.target.value },
                    }))
                  }
                  placeholder="#000000"
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">Line Width</label>
              <input
                type="number"
                min={0}
                step={0.1}
                className="form-control"
                value={form.lineStyle?.lineWidth ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    lineStyle: {
                      ...(p.lineStyle ?? {}),
                      lineWidth: e.target.value ? Number(e.target.value) : undefined,
                    },
                  }))
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Line Opacity</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.1}
                className="form-control"
                value={form.lineStyle?.lineOpacity ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    lineStyle: {
                      ...(p.lineStyle ?? {}),
                      lineOpacity: e.target.value ? Number(e.target.value) : undefined,
                    },
                  }))
                }
                placeholder="0–1"
              />
            </div>

            {/* Circle Style */}
            <div className="col-12">
              <h4 className="text-muted fs-5 mb-0">Circle Style</h4>
            </div>
            <div className="col-md-3">
              <label className="form-label">Circle Color</label>
              <div className="input-group">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={form.circleStyle?.circleColor ?? '#000000'}
                  onChange={e =>
                    setForm(p => ({
                      ...p,
                      circleStyle: { ...(p.circleStyle ?? {}), circleColor: e.target.value },
                    }))
                  }
                />
                <input
                  type="text"
                  className="form-control"
                  value={form.circleStyle?.circleColor ?? ''}
                  onChange={e =>
                    setForm(p => ({
                      ...p,
                      circleStyle: { ...(p.circleStyle ?? {}), circleColor: e.target.value },
                    }))
                  }
                  placeholder="#000000"
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">Circle Radius</label>
              <input
                type="number"
                min={0}
                className="form-control"
                value={form.circleStyle?.circleRadius ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    circleStyle: {
                      ...(p.circleStyle ?? {}),
                      circleRadius: e.target.value ? Number(e.target.value) : undefined,
                    },
                  }))
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Circle Opacity</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.1}
                className="form-control"
                value={form.circleStyle?.circleOpacity ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    circleStyle: {
                      ...(p.circleStyle ?? {}),
                      circleOpacity: e.target.value ? Number(e.target.value) : undefined,
                    },
                  }))
                }
                placeholder="0–1"
              />
            </div>

            {/* Raster */}
            <div className="col-12">
              <h4 className="text-muted fs-5 mb-0">Raster</h4>
            </div>
            <div className="col-md-2">
              <label className="form-label">Raster Opacity</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.1}
                className="form-control"
                value={form.rasterStyle?.rasterOpacity ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    rasterStyle: {
                      rasterOpacity: e.target.value ? Number(e.target.value) : undefined,
                    },
                  }))
                }
                placeholder="0–1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Feature Styles ── */}
      <div className="card mt-3">
        <div className="card-header">
          <h3 className="card-title">Feature Styles</h3>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label">Feature Style Key</label>
              <input
                type="text"
                className="form-control"
                value={
                  Array.isArray(form.featureStyleKey)
                    ? form.featureStyleKey.join(', ')
                    : (form.featureStyleKey ?? '')
                }
                onChange={e => {
                  const parts = e.target.value
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean);
                  setForm(p => ({
                    ...p,
                    featureStyleKey: parts.length > 1 ? parts : parts[0] || undefined,
                  }));
                }}
                placeholder="property name (comma-separated for composite key)"
              />
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-vcenter">
              <thead>
                <tr>
                  <th style={{ minWidth: 160 }}>Value (comma-sep for composite)</th>
                  <th style={{ minWidth: 200 }}>Label</th>
                  <th>Fill Color</th>
                  <th style={{ minWidth: 80 }}>Fill α</th>
                  <th>Line Color</th>
                  <th style={{ minWidth: 80 }}>Line W</th>
                  <th>Circle Color</th>
                  <th style={{ minWidth: 80 }}>Circle R</th>
                  <th className="w-1"></th>
                </tr>
              </thead>
              <tbody>
                {featureStyles.map((row, i) => {
                  const rawValue = Array.isArray(row.value) ? row.value.join(', ') : row.value;
                  return (
                    <tr key={i}>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={rawValue}
                          onChange={e => {
                            const parts = e.target.value.split(',').map(s => s.trim());
                            updateFeatureStyle(i, { value: parts.length > 1 ? parts : parts[0] });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={row.label}
                          onChange={e => updateFeatureStyle(i, { label: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          type="color"
                          className="form-control form-control-sm form-control-color"
                          style={{ width: 48 }}
                          value={row.fillStyle?.fillColor ?? '#cccccc'}
                          onChange={e => updateFeatureStyleFill(i, { fillColor: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          max={1}
                          step={0.1}
                          className="form-control form-control-sm"
                          value={row.fillStyle?.fillOpacity ?? ''}
                          onChange={e =>
                            updateFeatureStyleFill(i, {
                              fillOpacity: e.target.value ? Number(e.target.value) : undefined,
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="color"
                          className="form-control form-control-sm form-control-color"
                          style={{ width: 48 }}
                          value={row.lineStyle?.lineColor ?? '#000000'}
                          onChange={e => updateFeatureStyleLine(i, { lineColor: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          step={0.1}
                          className="form-control form-control-sm"
                          value={row.lineStyle?.lineWidth ?? ''}
                          onChange={e =>
                            updateFeatureStyleLine(i, {
                              lineWidth: e.target.value ? Number(e.target.value) : undefined,
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="color"
                          className="form-control form-control-sm form-control-color"
                          style={{ width: 48 }}
                          value={row.circleStyle?.circleColor ?? '#000000'}
                          onChange={e =>
                            updateFeatureStyleCircle(i, { circleColor: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          className="form-control form-control-sm"
                          value={row.circleStyle?.circleRadius ?? ''}
                          onChange={e =>
                            updateFeatureStyleCircle(i, {
                              circleRadius: e.target.value ? Number(e.target.value) : undefined,
                            })
                          }
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-icon text-danger"
                          onClick={() => removeFeatureStyle(i)}
                          type="button"
                        >
                          <IconTrash className="icon" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            className="btn btn-outline-primary btn-sm mt-2"
            onClick={addFeatureStyleRow}
            type="button"
          >
            <IconPlus className="icon" />
            Add Row
          </button>
        </div>
      </div>

      {/* ── Actions ── */}
      <div className="row mt-4 mb-5">
        <div className="col">
          <button
            className="btn btn-secondary me-2"
            onClick={() => navigate('/settings/map-layers')}
          >
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              'Save Layer'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
